// src/review/steps/SecurityReviewStep.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Grid, Alert, Text, Button, Group } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useReview } from '../context/ReviewContext';
import { updateReviewProgress } from '../../redux/actions/reviewActions';
import {
  fetchScenarios,
  fetchSecurityControls,
  selectScenarios,
  selectControls,
} from '../../redux/slices/securitySlice';
import { transformThreatsToFlatList, groupThreatsByCategory } from '../utils/dataTransformers';
import ThreatNavigator from '../canvas/navigation/ThreatNavigator';
import ThreatCanvas from '../canvas/ThreatCanvas';

/**
 * SecurityReviewStep - Step 2 of review flow
 * Shows all security threats with navigation and canvas
 */
function SecurityReviewStep() {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid, currentThreatId, selectThreat, goToStep } = useReview();

  // Get security scenarios and controls from Redux
  const scenarios = useSelector((state) => selectScenarios(state, assessmentUuid));
  const securityControls = useSelector((state) => selectControls(state, assessmentUuid));

  // Build security state object for transformThreatsToFlatList
  const securityState = useMemo(() => ({
    scenarios: scenarios,
  }), [scenarios]);

  // Transform to flat list and enrich with controls
  const allThreats = useMemo(() => {
    const threats = transformThreatsToFlatList(securityState);

    // Enrich each threat with its controls
    return threats.map(threat => ({
      ...threat,
      controls: securityControls[threat.scenarioId] || [],
    }));
  }, [securityState, securityControls]);

  const threatsByCategory = useMemo(() =>
    groupThreatsByCategory(allThreats),
    [allThreats]
  );

  // Current threat being viewed
  const [currentThreatIndex, setCurrentThreatIndex] = useState(0);
  const currentThreat = allThreats[currentThreatIndex];

  // Refs to prevent infinite loops
  const initialThreatSelectedRef = useRef(false);
  const fetchedScenariosRef = useRef(new Set());

  // Fetch threat data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (modelId && assessmentUuid) {
        try {
          await dispatch(fetchScenarios({ modelId, assessmentUuid }));
        } catch (error) {
          console.error('[SecurityReviewStep] Error fetching threat data:', error);
        }
      }
    };

    fetchData();
  }, [modelId, assessmentUuid, dispatch]);

  // Fetch security controls for each scenario
  useEffect(() => {
    const scenariosObj = securityState.scenarios || {};
    const scenarioIds = Object.keys(scenariosObj);

    scenarioIds.forEach((scenarioId) => {
      // Only fetch if not already fetched
      if (!fetchedScenariosRef.current.has(scenarioId)) {
        dispatch(fetchSecurityControls({ modelId, assessmentUuid, scenarioId }));
        fetchedScenariosRef.current.add(scenarioId);
      }
    });
  }, [dispatch, modelId, assessmentUuid, securityState.scenarios]);

  // Set first threat as current when data loads
  useEffect(() => {
    if (allThreats.length > 0 && !currentThreatId && !initialThreatSelectedRef.current) {
      const firstThreat = allThreats[0];
      selectThreat(firstThreat.threatId);
      dispatch(updateReviewProgress(modelId, 'security', firstThreat.threatId));
      initialThreatSelectedRef.current = true; // Prevent re-firing
    }
  }, [allThreats, currentThreatId, selectThreat, dispatch, modelId]);

  // Handle threat selection from navigator
  const handleThreatSelect = useCallback((threat) => {
    const index = allThreats.findIndex(t => t.threatId === threat.threatId);
    if (index >= 0) {
      setCurrentThreatIndex(index);
      selectThreat(threat.threatId);
      dispatch(updateReviewProgress(modelId, 'security', threat.threatId));
    }
  }, [allThreats, selectThreat, dispatch, modelId]);

  // Navigate to next threat
  const handleNext = useCallback(() => {
    const nextIndex = currentThreatIndex + 1;
    if (nextIndex < allThreats.length) {
      setCurrentThreatIndex(nextIndex);
      const nextThreat = allThreats[nextIndex];
      selectThreat(nextThreat.threatId);
      dispatch(updateReviewProgress(modelId, 'security', nextThreat.threatId));
    } else {
      // All threats reviewed - move to summary step
      goToStep('summary');
    }
  }, [currentThreatIndex, allThreats, selectThreat, dispatch, modelId, goToStep]);

  // Navigate to previous threat
  const handlePrevious = useCallback(() => {
    const prevIndex = currentThreatIndex - 1;
    if (prevIndex >= 0) {
      setCurrentThreatIndex(prevIndex);
      const prevThreat = allThreats[prevIndex];
      selectThreat(prevThreat.threatId);
      dispatch(updateReviewProgress(modelId, 'security', prevThreat.threatId));
    }
  }, [currentThreatIndex, allThreats, selectThreat, dispatch, modelId]);

  // Skip current threat
  const handleSkip = useCallback(() => {
    handleNext();
  }, [handleNext]);

  // Validation
  if (allThreats.length === 0) {
    return (
      <Stack gap="lg">
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          No security threats found for review. Complete the security assessment first.
        </Alert>
        <Button onClick={() => goToStep('summary')}>
          Skip to Summary
        </Button>
      </Stack>
    );
  }

  // Don't render navigation until data is stable
  if (!allThreats || allThreats.length === 0) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: '60vh' }}>
        <Alert color="blue" icon={<IconAlertCircle size={16} />}>
          Loading security threats...
        </Alert>
      </Stack>
    );
  }

  if (!currentThreat) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        Loading threat data...
      </Alert>
    );
  }

  return (
    <Grid gutter="md">
      {/* Left sidebar - Threat Navigator */}
      <Grid.Col span={{ base: 12, md: 3 }}>
        <ThreatNavigator
          threatsByCategory={threatsByCategory}
          currentThreat={currentThreat}
          onThreatSelect={handleThreatSelect}
        />
      </Grid.Col>

      {/* Right panel - Threat Canvas */}
      <Grid.Col span={{ base: 12, md: 9 }}>
        <Stack gap="lg">
          {/* Progress indicator */}
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Threat {currentThreatIndex + 1} of {allThreats.length}
            </Text>
            <Button
              variant="light"
              onClick={() => goToStep('summary')}
            >
              Complete Security Review → Summary
            </Button>
          </Group>

          {/* Threat Canvas */}
          <ThreatCanvas
            threat={currentThreat}
            onNext={handleNext}
            onPrevious={currentThreatIndex > 0 ? handlePrevious : null}
            onSkip={handleSkip}
          />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

export default SecurityReviewStep;
